import { useMemo } from 'react'
import { useLocation } from 'react-router'
import * as S from './HomePageContainer.styles'
import HomePageUI from './HomePageUI'
import useHomePage from './useHomePage'
import { SCREENS } from 'services/Analytics/Analytics'
import { PlaybackProvider } from '../../components/PlaybackProvider/PlaybackContext'
import BrowserThemeColor from 'components/ThemeColor/BrowserThemeColor'
import StickyCta from '../../components/StickyCta/StickyCta'
import useScreenSize from '../../hooks/useScreenSize'
import TopBar, { type TopBarProps } from '../../elements/TopBar/TopBar'
import MixtilesLogo from '../../elements/TopBar/Logo/MixtilesLogo'
import { photosTheme as theme } from 'mixtiles-web-common'
import { PHOTO_STYLER_URL, PRODUCTS_URL } from '../../config/config'
import TopBarBanner from 'elements/TopBar/TopBarBanner/TopBarBanner'
import { useExperimentManager } from '../../services/ExperimentManager/ExperimentManager'
import { useTranslation } from 'react-i18next'
import { genericBannerManager } from '../../services/GenericBannerManager'
import {
  usePhotoBookInProgress,
  useTilesInProgress,
} from '../../hooks/useWorkInProgress'
import PBHomepageBanner from '../../components/PhotoBook/PBHomepageBanner/PBHomepageBanner'
import { MULTI_PRODUCT_HOMEPAGE_SOURCE } from '../MultiProductHomePage/multiProductHomePage.consts'

type Props = {
  heroVideoCldId: string
}

export default function TilesHomePageContainer({ heroVideoCldId }: Props) {
  const { t } = useTranslation()
  const experimentManager = useExperimentManager()
  const isMediumScreen = useScreenSize('md')
  const hasTilesInProgress = useTilesInProgress()
  const hasPhotoBookInProgress = usePhotoBookInProgress()
  const isPhotoBooksCoreIntegrationOn =
    experimentManager.isEnabled('photo-books')
  const showPhotoBooksBanner = experimentManager.isEnabled(
    'homepage-photo-books-banner'
  )

  const location = useLocation()
  const isFromMultiProductHomepage =
    location.state?.source === MULTI_PRODUCT_HOMEPAGE_SOURCE
  const { onMainCtaClick } = useHomePage({
    screenName: SCREENS.HOMEPAGE,
    eventProperties: {
      'Product Type': 'Single Tiles',
    },
    ctaClickUrl:
      isPhotoBooksCoreIntegrationOn && !isFromMultiProductHomepage
        ? PRODUCTS_URL
        : PHOTO_STYLER_URL,
  })

  const ctaText = useMemo(() => {
    if (hasTilesInProgress || hasPhotoBookInProgress) {
      return t('general.continue_design')
    }

    return t('homepage.main_button_cta')
  }, [hasTilesInProgress, hasPhotoBookInProgress, t])

  const topBarProps: TopBarProps = useMemo(() => {
    if (isMediumScreen) {
      return {
        backgroundColor: 'transparent',
        noBorder: true,
        absolute: true,
        iconsColor: 'white',
      }
    }
    return {
      backgroundColor: showPhotoBooksBanner
        ? theme.colors.grayAccent
        : theme.colors.white,
      hasRoundedTopCorners: showPhotoBooksBanner,
    }
  }, [isMediumScreen, showPhotoBooksBanner])

  const TopBarComponent = isMediumScreen
    ? TopBar.TopContent
    : TopBar.BottomContent

  return (
    <S.Container className="HomePage">
      <BrowserThemeColor color={theme.colors.white} />
      <TopBar {...topBarProps}>
        {showPhotoBooksBanner && (
          <TopBar.TopContent>
            <PBHomepageBanner />
          </TopBar.TopContent>
        )}
        <TopBarComponent>
          <TopBarBanner bannerManagerInstance={genericBannerManager} />
        </TopBarComponent>
        {isMediumScreen && (
          <TopBar.TitleContent>
            <MixtilesLogo color="white" />
          </TopBar.TitleContent>
        )}
      </TopBar>
      {!isMediumScreen && (
        <StickyCta
          onClick={onMainCtaClick}
          text={ctaText}
          show
          animate={false}
        />
      )}
      <PlaybackProvider>
        <HomePageUI
          ctaProps={{
            ctaText,
            onClick: onMainCtaClick,
          }}
          heroVideoCldId={heroVideoCldId}
        />
      </PlaybackProvider>
    </S.Container>
  )
}
